import { TooltipDirective } from '../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../componentes/botones';
import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { CardHeaderComponent } from '../../../../../componentes/card-header/card-header.component';
import { ContentCardComponent } from '../../../../../componentes/content-card/content-card.component';
import { ILogReenvio } from 'modelos/src';
import { JsonTreeComponent } from '../../../logs/json-tree/json-tree.component';

type Seccion = 'todo' | 'headers' | 'body' | 'respuesta';

/**
 * Detalle de un log de reenvío: destino, método, headers y body tal cual
 * salieron, con copia en JSON — completo o por sección — para pasárselo al
 * proveedor. Los headers van sin enmascarar (credenciales incluidas): es
 * justamente lo que el proveedor necesita para validar la autenticación.
 */
@Component({
  selector: 'app-ver-body-dialog',
  templateUrl: './ver-body-dialog.component.html',
  styleUrl: './ver-body-dialog.component.scss',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [TooltipDirective, 
    DatePipe,
    MatCardModule,
    BOTONES,
    MatIconModule,
    
    CardHeaderComponent,
    ContentCardComponent,
    JsonTreeComponent,
  ],
})
export class VerBodyDialogComponent implements OnInit {
  log = inject<ILogReenvio>(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<VerBodyDialogComponent>>(MatDialogRef);

  body = this.log?.body ?? '';
  parsedJson: any = null;
  formattedBody = '';
  /** Sección recién copiada, para el tilde del botón. */
  copiado?: Seccion;
  /** Nodos desplegados del árbol, por ruta. Ver `rutasExpandidas`. */
  expandido: Record<string, boolean> = {};

  /** Lo que contestó el destino (solo reenvíos HTTP, logs nuevos). */
  respuesta = this.log?.respuesta ?? '';
  respuestaJson: any = null;
  respuestaTexto = '';
  expandidoRespuesta: Record<string, boolean> = {};

  get respuestaStatus(): number | undefined {
    return this.log?.respuestaStatus;
  }

  /** 2xx en verde, 4xx/5xx en rojo. Un 200 igual puede traer el rechazo
   *  adentro del cuerpo (Suite911, Soflex): por eso se muestra el cuerpo. */
  get claseStatus(): string {
    const s = this.respuestaStatus ?? 0;
    if (s >= 200 && s < 300) return 'status-ok';
    if (s >= 400) return 'status-error';
    return '';
  }

  /** Los reenvíos HTTP guardan la URL completa en `host`; los de socket,
   *  host y puerto por separado. */
  get esHttp(): boolean {
    return /^https?:\/\//.test(this.log?.host ?? '');
  }

  get destino(): string {
    if (this.esHttp) return this.log.host ?? '';
    return [this.log?.host, this.log?.puerto]
      .filter((v) => v != null && v !== '')
      .join(':');
  }

  get headers(): [string, string][] {
    return Object.entries(this.log?.headers ?? {});
  }

  get entidad(): string | undefined {
    return this.log?.tracker?.nombre ?? this.log?.dispositivoAlarma?.nombre;
  }

  cerrar(): void {
    this.dialogRef.close();
  }

  ngOnInit(): void {
    if (this.respuesta) {
      const r = this.parsear(this.respuesta);
      this.respuestaJson = r.json;
      this.respuestaTexto = r.texto;
      if (r.json !== null)
        this.expandidoRespuesta = this.rutasExpandidas(r.json);
    }
    if (!this.body) return;
    const b = this.parsear(this.body);
    this.parsedJson = b.json;
    this.formattedBody = b.texto;
    if (b.json !== null) this.expandido = this.rutasExpandidas(b.json);
  }

  /** JSON → objeto (para el árbol); XML → indentado; el resto, tal cual. */
  private parsear(texto: string): { json: any; texto: string } {
    try {
      return { json: JSON.parse(texto), texto: '' };
    } catch {}
    return {
      json: null,
      texto: texto.trimStart().startsWith('<') ? this.formatXml(texto) : texto,
    };
  }

  /** El reenvío completo como JSON. El body va parseado si es JSON, así el
   *  proveedor lo lee como objeto y no como un string escapado. */
  private reenvioComoJson(): Record<string, unknown> {
    return {
      fecha: this.log?.fecha,
      protocolo: this.log?.protocolo,
      metodo: this.log?.metodoHttp,
      url: this.destino,
      headers: this.log?.headers,
      body: this.parsedJson ?? (this.body || undefined),
      respuesta:
        this.respuestaStatus != null || this.respuesta
          ? {
              status: this.respuestaStatus,
              body: this.respuestaJson ?? (this.respuesta || undefined),
            }
          : undefined,
      ack: this.log?.ack,
      error: this.log?.error,
    };
  }

  copiar(seccion: Seccion): void {
    let texto: string;
    switch (seccion) {
      case 'todo':
        texto = JSON.stringify(this.reenvioComoJson(), null, 2);
        break;
      case 'headers':
        texto = JSON.stringify(this.log?.headers ?? {}, null, 2);
        break;
      case 'body':
        texto = this.parsedJson
          ? JSON.stringify(this.parsedJson, null, 2)
          : this.body;
        break;
      case 'respuesta':
        texto = this.respuestaJson
          ? JSON.stringify(this.respuestaJson, null, 2)
          : this.respuesta;
        break;
    }
    navigator.clipboard.writeText(texto).then(() => {
      this.copiado = seccion;
      setTimeout(() => {
        if (this.copiado === seccion) this.copiado = undefined;
      }, 2000);
    });
  }

  /**
   * El diálogo existe para leer el body: se abre con todo desplegado en vez de
   * pedir un clic por nivel. El body de Soflex, por ejemplo, es
   * `{"positions":[{...}]}` y sin esto hacían falta dos clics para ver un
   * campo. El tope de profundidad es por las dudas: un body raro no debería
   * poder colgar el diálogo.
   */
  private rutasExpandidas(
    valor: any,
    path = 'root',
    profundidad = 0,
    acc: Record<string, boolean> = {},
  ): Record<string, boolean> {
    if (profundidad >= 6 || !valor || typeof valor !== 'object') return acc;
    for (const key of Object.keys(valor)) {
      const hijo = valor[key];
      if (hijo && typeof hijo === 'object' && Object.keys(hijo).length > 0) {
        const ruta = `${path}.${key}`;
        acc[ruta] = true;
        this.rutasExpandidas(hijo, ruta, profundidad + 1, acc);
      }
    }
    return acc;
  }

  private formatXml(xml: string): string {
    let formatted = '';
    let indent = 0;
    const tab = '  ';
    xml
      .replace(/>\s*</g, '><')
      .split(/(<[^>]+>)/)
      .filter(Boolean)
      .forEach((token) => {
        if (/^<\//.test(token)) {
          indent--;
          formatted += tab.repeat(Math.max(0, indent)) + token + '\n';
        } else if (/^<[^?!][^>]*[^/]>$/.test(token)) {
          formatted += tab.repeat(indent) + token + '\n';
          indent++;
        } else if (/^<[^?!][^>]*\/>$/.test(token)) {
          formatted += tab.repeat(indent) + token + '\n';
        } else if (/^<[?!]/.test(token)) {
          formatted += tab.repeat(indent) + token + '\n';
        } else {
          const text = token.trim();
          if (text) formatted += tab.repeat(indent) + text + '\n';
        }
      });
    return formatted.trim();
  }
}
